import { getCanonicalPageUrl } from '@findy-code/utils/next-js';
import { runWithSentry } from '@findy-code/utils/sentry';
import type {
  FetchJobDescriptionSpeakerDeckOembedQuery,
  FetchPublicJobDescriptionQuery,
} from '@findy-code/yuffie/feature-company-job-description-detail';
import {
  CompanyJobDescriptionDetailV2Container,
  FetchJobDescriptionSpeakerDeckOembedDocument,
  FetchPublicJobDescriptionDocument,
} from '@findy-code/yuffie/feature-company-job-description-detail';
import { LayoutContainer } from '@findy-code/yuffie/feature-layout';
import { createSsrClient } from '@findy-code/yuffie/graphql';
import type {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from 'next';
import Head from 'next/head';

export const getServerSideProps = async ({
  query,
  req,
  resolvedUrl,
}: GetServerSidePropsContext) => {
  const url = getCanonicalPageUrl({ resolvedUrl });
  const { jd_hash: jdHash, from: mailReferrer, companyId } = query;

  try {
    const { data } = await runWithSentry(
      createSsrClient(req).query<FetchPublicJobDescriptionQuery | undefined>({
        errorPolicy: 'none',
        query: FetchPublicJobDescriptionDocument,
        variables: {
          jdHash,
        },
      })
    );

    const jobDescription = data?.jobDescription ?? null;

    let jobDescriptionSpeakerDeckOembed: FetchJobDescriptionSpeakerDeckOembedQuery['jobDescriptionSpeakerDeckOembed'] =
      null;
    if (jobDescription?.jobDescriptionIntroductionSlide?.url) {
      const { data: jobDescriptionSpeakerDeckOembedData } = await runWithSentry(
        createSsrClient(req).query<
          FetchJobDescriptionSpeakerDeckOembedQuery | undefined
        >({
          query: FetchJobDescriptionSpeakerDeckOembedDocument,
          variables: {
            jdHash,
          },
        })
      );
      jobDescriptionSpeakerDeckOembed =
        jobDescriptionSpeakerDeckOembedData?.jobDescriptionSpeakerDeckOembed ??
        null;
    }

    const title = jobDescription?.title ?? '';
    const description = jobDescription?.jobDescription ?? '';
    const companyName = jobDescription?.company?.name ?? '';
    const jobType = jobDescription?.jobType?.name ?? '';
    const isFindyUs = jobDescription?.company.isFindyUs ?? false;
    const isPublic = jobDescription?.isPublic ?? false;
    const ogpImage = jobDescription?.company.images?.[0]?.url ?? null;

    if (
      typeof companyId === 'string' &&
      !isNaN(parseInt(companyId, 10)) &&
      parseInt(companyId, 10) !== jobDescription?.company?.id
    ) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        companyName,
        description,
        isNoIndex: !isPublic || isFindyUs,
        jdHash: typeof jdHash === 'string' ? jdHash : '',
        jobDescription,
        jobDescriptionSpeakerDeckOembed,
        jobType,
        mailReferrer: typeof mailReferrer === 'string' ? mailReferrer : '',
        ogpImage,
        title,
        url,
        redirectPath: resolvedUrl,
      },
    };
  } catch {
    return {
      props: {
        companyName: '',
        description: '',
        isNoIndex: true,
        jdHash: typeof jdHash === 'string' ? jdHash : '',
        jobDescription: null,
        jobDescriptionSpeakerDeckOembed: null,
        jobType: '',
        mailReferrer: typeof mailReferrer === 'string' ? mailReferrer : '',
        ogpImage: null,
        title: '',
        url,
        redirectPath: '',
      },
    };
  }
};

type Props = InferGetServerSidePropsType<typeof getServerSideProps> & {
  isLogin: boolean;
  isLoadingUserData: boolean;
};

export const CompaniesJobDescriptionsPage = ({
  mailReferrer,
  companyName,
  jobType,
  title,
  description,
  url,
  ogpImage,
  isNoIndex,
  isLogin,
  isLoadingUserData,
  jdHash,
  jobDescription,
  jobDescriptionSpeakerDeckOembed,
  redirectPath,
}: Props) => {
  return (
    <>
      <Head>
        {jobType ? (
          <title>{`${jobType}の募集 | ${companyName}`}</title>
        ) : companyName ? (
          <title>{`${companyName}`}</title>
        ) : null}
        {title && (
          <>
            <meta content={title} key="og:title" property="og:title" />
            <meta content={title} name="twitter:title" />
          </>
        )}
        {description && (
          <>
            <meta content={description} name="description" />
            <meta
              content={description}
              key="og:description"
              property="og:description"
            />
            <meta content={description} name="twitter:description" />
          </>
        )}
        {url && (
          <>
            <meta content={url} key="og:url" property="og:url" />
            <link href={url} rel="canonical" />
          </>
        )}
        <meta content="article" key="og:type" property="og:type" />
        {ogpImage && (
          <>
            <meta content={ogpImage} key="og:image" property="og:image" />
            <meta content={ogpImage} name="twitter:image" />
            <meta content={ogpImage} itemProp="image" />
          </>
        )}
        {isNoIndex && (
          <meta content="noindex" data-testid="meta-robots" name="robots" />
        )}
      </Head>
      <LayoutContainer
        isShowLoginButton
        isWithBottomMargin
        isLoadingUserData={isLoadingUserData}
        isLogin={isLogin}
      >
        <CompanyJobDescriptionDetailV2Container
          isLogin={isLogin}
          jdHash={jdHash}
          jobDescription={jobDescription}
          jobDescriptionSpeakerDeckOembed={jobDescriptionSpeakerDeckOembed}
          mailReferrer={mailReferrer}
          redirectPath={redirectPath}
        />
      </LayoutContainer>
    </>
  );
};

export default CompaniesJobDescriptionsPage;
